import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Link2, Unlink, Loader2, CheckCircle2,
  AlertCircle, ExternalLink, Megaphone, ShieldCheck
} from 'lucide-react';
import AppLayout from '../../layouts/AppLayout';
import { metaAdsService, googleAdsService } from '../../services';
import './styles.css';

/* ── Platform configs ────────────────── */
const PLATFORMS = [
  {
    id: 'meta',
    name: 'Meta Ads',
    subtitle: 'Facebook · Instagram · Messenger · Audience Network',
    logo: '/img/meta-logo.svg',
    fallbackEmoji: '📘',
    color: '#1877F2',
    gradient: 'linear-gradient(135deg, #1877F2, #0C63D4)',
    features: [
      'Campanhas no Facebook & Instagram',
      'Segmentação avançada por interesses',
      'Remarketing com Pixel',
      'Stories, Feed e Reels Ads',
    ],
  },
  {
    id: 'google',
    name: 'Google Ads',
    subtitle: 'Pesquisa · Display · YouTube · Shopping · Performance Max',
    logo: '/img/google-ads-logo.svg',
    fallbackEmoji: '🔍',
    color: '#4285F4',
    gradient: 'linear-gradient(135deg, #4285F4, #0F9D58)',
    features: [
      'Anúncios na Pesquisa Google',
      'Campanhas no YouTube',
      'Display e Shopping Ads',
      'Performance Max com IA do Google',
    ],
  },
];

function AdsConnectPage() {
  const navigate = useNavigate();

  const [metaStatus, setMetaStatus] = useState({ connected: false, loading: true });
  const [googleStatus, setGoogleStatus] = useState({ connected: false, loading: true });
  const [disconnecting, setDisconnecting] = useState(null);

  /* ── Load connection statuses ─────── */
  const loadStatuses = useCallback(async () => {
    const [metaRes, googleRes] = await Promise.allSettled([
      metaAdsService.getStatus(),
      googleAdsService.getStatus(),
    ]);

    setMetaStatus({
      connected: !!metaRes.value?.connected,
      pageName: metaRes.value?.meta_page_name,
      adAccountId: metaRes.value?.meta_ad_account_id,
      loading: false,
    });

    setGoogleStatus({
      connected: !!googleRes.value?.connected,
      customerId: googleRes.value?.customer_id,
      accountName: googleRes.value?.account_name,
      loading: false,
    });
  }, []);

  useEffect(() => { loadStatuses(); }, [loadStatuses]);

  /* ── Connect handler ───────────────── */
  const handleConnect = async (platformId) => {
    try {
      const service = platformId === 'meta' ? metaAdsService : googleAdsService;
      const data = await service.getConnectUrl();
      const url = data?.auth_url || data?.url;
      if (url) window.location.assign(url);
    } catch {
      /* noop */
    }
  };

  /* ── Disconnect handler ────────────── */
  const handleDisconnect = async (platformId) => {
    setDisconnecting(platformId);
    try {
      if (platformId === 'meta') {
        await metaAdsService.disconnect();
        setMetaStatus({ connected: false, loading: false });
      } else {
        // Google disconnect not available in service — reload status
        setGoogleStatus({ connected: false, loading: false });
      }
    } catch { /* noop */ }
    finally { setDisconnecting(null); }
  };

  /* ── Helpers ────────────────────────── */
  const getStatus = (id) => (id === 'meta' ? metaStatus : googleStatus);

  const connectedCount = [metaStatus, googleStatus].filter(s => s.connected).length;

  return (
    <AppLayout>
      <div className="ads-connect">
        {/* Header */}
        <header className="ads-connect__header">
          <button className="ads-create__back" onClick={() => navigate('/ads-manager')}>
            <ArrowLeft size={18} /> Voltar
          </button>
          <div>
            <h1 className="ads-connect__title"><Link2 size={24} /> Conectar Contas de Anúncios</h1>
            <p className="ads-connect__subtitle">
              Conecte suas contas para criar e gerenciar anúncios diretamente do MarketingPro
            </p>
          </div>
        </header>

        {/* Status bar */}
        <div className="ads-connect__status-bar">
          <ShieldCheck size={18} />
          <span>
            {connectedCount === 0
              ? 'Nenhuma conta conectada — conecte pelo menos uma para começar'
              : connectedCount === 2
                ? 'Todas as contas conectadas!'
                : `${connectedCount} de 2 contas conectadas`}
          </span>
          <div className="ads-connect__dots">
            <span className={`ads-connect__dot ${metaStatus.connected ? 'on' : ''}`} />
            <span className={`ads-connect__dot ${googleStatus.connected ? 'on' : ''}`} />
          </div>
        </div>

        {/* Platform cards */}
        <div className="ads-connect__grid">
          {PLATFORMS.map((p) => {
            const status = getStatus(p.id);
            const isLoading = status.loading;
            const isConnected = status.connected;
            const isDisconnecting = disconnecting === p.id;

            return (
              <div key={p.id} className={`ads-connect__card ${isConnected ? 'connected' : ''}`}>
                {/* Card header */}
                <div className="ads-connect__card-top" style={{ background: p.gradient }}>
                  <div className="ads-connect__card-logo">
                    <span className="ads-connect__card-emoji">{p.fallbackEmoji}</span>
                  </div>
                  <div>
                    <h2 className="ads-connect__card-name">{p.name}</h2>
                    <p className="ads-connect__card-sub">{p.subtitle}</p>
                  </div>
                  {isConnected && (
                    <span className="ads-connect__badge">
                      <CheckCircle2 size={14} /> Conectado
                    </span>
                  )}
                </div>

                {/* Card body */}
                <div className="ads-connect__card-body">
                  {/* Features list */}
                  <ul className="ads-connect__features">
                    {p.features.map((f, i) => (
                      <li key={i}><CheckCircle2 size={14} /> {f}</li>
                    ))}
                  </ul>

                  {/* Account info if connected */}
                  {isConnected && (
                    <div className="ads-connect__acct-info">
                      <span className="ads-connect__acct-label">Conta vinculada:</span>
                      <span className="ads-connect__acct-value">
                        {p.id === 'meta'
                          ? status.pageName || status.adAccountId || 'Conta Meta conectada'
                          : status.accountName || status.customerId || 'Conta Google conectada'}
                      </span>
                    </div>
                  )}

                  {/* Actions */}
                  <div className="ads-connect__card-actions">
                    {isLoading ? (
                      <div className="ads-connect__loading">
                        <Loader2 size={20} className="spin" /> Verificando...
                      </div>
                    ) : isConnected ? (
                      <>
                        <button
                          className="ads-connect__btn-create"
                          onClick={() => navigate(`/ads/create?platform=${p.id}`)}
                        >
                          <Megaphone size={16} /> Criar Anúncio
                        </button>
                        <button
                          className="ads-connect__btn-disconnect"
                          onClick={() => handleDisconnect(p.id)}
                          disabled={isDisconnecting}
                        >
                          {isDisconnecting
                            ? <><Loader2 size={14} className="spin" /> Desconectando...</>
                            : <><Unlink size={14} /> Desconectar</>}
                        </button>
                      </>
                    ) : (
                      <button
                        className="ads-connect__btn-connect"
                        style={{ background: p.gradient }}
                        onClick={() => handleConnect(p.id)}
                      >
                        <ExternalLink size={16} /> Conectar {p.name}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Help box */}
        <div className="ads-connect__help">
          <AlertCircle size={18} />
          <div>
            <strong>Como funciona?</strong>
            <p>
              Ao conectar sua conta, você autoriza o MarketingPro a criar e gerenciar campanhas
              em seu nome. Você pode desconectar a qualquer momento. Seus dados de pagamento
              permanecem exclusivamente na plataforma original (Meta/Google).
            </p>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}

export default AdsConnectPage;
